import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loading, Tabs, Tooltip } from "@foris/avocado-suite";
import ProgrammingEdit from "./programming-edit";
import { AppContext } from "../context/EditSessionsContext";
import LinkHeader from "@dimensions/link/components/HeaderLink";
import FormEdit from "./FormEdit/FormEdit";
import ResultEdit from "./ResultEdit/ResultEdit";
import {
  useProgrammingEditStore,
  clearProgrammingResults,
  resetProgrammingEditStore,
} from "@context/useProgrammingEditStore";
import AdvanceSearchInstructor from "./FormEdit/instructor-advanced-search";
import AdvanceSearchClassroom from "../pages/FormEdit/Classroom/AdvanceSearch";
import { useGetSessionsRecommendations } from "../hooks/useGetSessionsRecommendation";
import { useListenEvent } from "@common/hooks/useListenEvent";
import { Types } from "../context/linkData.reducer";
import { ContextApp } from "@config/Context/contextApp";
import css from "./../pages/FormEdit/formEdit.module.scss";
import { isCourseTypeForAssignationOrProgramming } from "@utils/courseUtils";
import { isLinkStarted } from "../utils/isLinkStarted";

const InitEditSessions = () => {
  const { t } = useTranslation();
  const { user } = useContext(ContextApp);
  const { dispatch, state } = useContext(AppContext);
  const isProgrammingResultsActive = useProgrammingEditStore(s => s.isProgrammingResultsActive);
  const hasChanges = useProgrammingEditStore(s => s.hasChanges);
  const [
    {
      response: recommendations,
      hasErrors: hasRecommendationsErrors,
      isLoading: isLoadingRecommendations,
    },
    getRecomendations,
  ] = useGetSessionsRecommendations();
  const [heightHeader, setHeightHeder] = useState(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const canEditProgramming =
    user?.abilities?.can_edit_programming &&
    isCourseTypeForAssignationOrProgramming(state?.link?.info?.course?.courseType);

  const someSectionsWithUnnasignedSessions = useMemo(
    () => state?.link?.sections?.some(section => section?.unasignedSessions?.length > 0),
    [state?.link?.sections],
  );

  const isProgrammingBlockedByStartedLink = useMemo(
    () =>
      isLinkStarted(state?.link?.sections, state?.form?.blockingThreshold) &&
      !user?.abilities?.can_edit_programming_for_started_links,
    [
      state?.link?.sections,
      state?.form?.blockingThreshold,
      user?.abilities?.can_edit_programming_for_started_links,
    ],
  );

  const isProgrammingTabDisabled =
    state?.page?.active?.results ||
    someSectionsWithUnnasignedSessions ||
    isProgrammingBlockedByStartedLink;

  const programmingTabTooltipLabel = isProgrammingBlockedByStartedLink
    ? t("programming-edit.tabs.programming-started-tooltip")
    : t("programming-edit.tabs.programming-disabled-tooltip");

  const isProgrammingTabTooltipVisible =
    isProgrammingBlockedByStartedLink || someSectionsWithUnnasignedSessions;

  useListenEvent({
    eventKey: "on:navigate",
    handler: useCallback(detail => {
      if (detail.target === "assignment-tab") setActiveTabIndex(0);
    }, []),
  });

  /**
   * Requets for sessions recommendations after the link info is obtained.
   */
  useEffect(() => {
    window.addEventListener("load-recommendations", (data: any) => {
      getRecomendations(data?.detail?.sessionIds);
    });

    return () => {
      window.removeEventListener("load-recommendations", (data: any) => {
        getRecomendations(data?.detail?.sessionIds);
      });
    };
  }, []);

  /** Reset the programming store when navigating away from the page entirely. */
  useEffect(() => {
    return () => {
      resetProgrammingEditStore();
    };
  }, []);

  useEffect(() => {
    dispatch({ type: Types.SetRecommendations, payload: recommendations });
  }, [recommendations]);

  useEffect(() => {
    dispatch({ type: Types.SetHasErrorsRecommendations, payload: hasRecommendationsErrors });
  }, [hasRecommendationsErrors]);

  useEffect(() => {
    dispatch({ type: Types.SetIsLoadingRecommendations, payload: isLoadingRecommendations });
  }, [isLoadingRecommendations]);

  const isAdvancedSearchVisible =
    state?.page?.active?.advanceSearchClassroom || state?.page?.active?.advanceSearchInstructor;

  return (
    <main className={css.main}>
      {state?.link?.info && <LinkHeader link={state?.link?.info} setHeight={setHeightHeder} />}
      {state?.page?.loading && <Loading />}
      <Tabs
        className={css.editionTypeTabs}
        activeIndex={activeTabIndex}
        onClick={index => {
          setActiveTabIndex(index);
          clearProgrammingResults();
        }}
        items={[
          {
            label: t("programming-edit.tabs.assignation"),
            disabled: isProgrammingResultsActive,
            children: (
              <>
                {[state?.page?.active?.form, isAdvancedSearchVisible].includes(true) && (
                  <div className={isAdvancedSearchVisible && css.contentHidden}>
                    <FormEdit heightHeader={heightHeader} />
                  </div>
                )}
                {state?.page?.active?.results && <ResultEdit heightHeader={heightHeader} />}
                {state?.page?.active?.advanceSearchClassroom && <AdvanceSearchClassroom />}
                {state?.page?.active?.advanceSearchInstructor && <AdvanceSearchInstructor />}
              </>
            ),
          },
          ...(canEditProgramming
            ? [
                {
                  disabled: isProgrammingTabDisabled,
                  label: (
                    <Tooltip
                      label={programmingTabTooltipLabel}
                      visible={isProgrammingTabTooltipVisible ? undefined : false}
                    >
                      {t("programming-edit.tabs.programming")}
                      {hasChanges && <span className={css.programmingChangesDot} />}
                    </Tooltip>
                  ),
                  children: isProgrammingResultsActive ? (
                    <ResultEdit heightHeader={heightHeader} />
                  ) : (
                    <ProgrammingEdit />
                  ),
                },
              ]
            : []),
        ]}
      />
    </main>
  );
};

export default InitEditSessions;
